import { Timeline, Typography } from 'antd';
import { createUseStyles } from 'react-jss';
import { shipiumTheme } from '@shipium-corp/shipium-ui-theme';
import { CollapsiblePool } from './CollapsiblePool';
import { RuleTimelineItem } from './RuleTimelineItem';
import { resolveTimelineEntry, type ProcessedTimeline } from './ruleTimeline';
import { BranchesOutlined } from '@ant-design/icons';

const { Text } = Typography;

const inlineSectionLabelStyle: React.CSSProperties = {
  display: 'inline-block',
  marginBottom: 8,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: shipiumTheme.token.colorPrimary,
  fontWeight: 600,
};

const useTimelineStyles = createUseStyles({
  flush: {
    marginBottom: 0,
    paddingLeft: 8,
    '& .ant-timeline-item-last': {
      paddingBottom: 0,
    },
    '& .ant-timeline-item-last > .ant-timeline-item-content': {
      minHeight: 0,
    },
    '& .ant-timeline-item-head-custom': {
      background: 'transparent',
    },
  },
});

export interface ExecutionTraceProps {
  timeline: ProcessedTimeline;
  methodNameById: Record<string, string>;
}

const toPoolMethods = (ids: string[], methodNameById: Record<string, string>): { id: string; name: string }[] =>
  ids.map((id) => ({ id, name: methodNameById[id] ?? id }));

export const ExecutionTrace = ({ timeline, methodNameById }: ExecutionTraceProps) => {
  const styles = useTimelineStyles();
  const {
    initialPool,
    finalPool,
    selectedMethodId,
    timeline: allRules,
    beforeTimeline,
    postTerminatorItems,
  } = timeline;

  const initialPoolMethods = toPoolMethods(initialPool, methodNameById);
  const finalPoolMethods = toPoolMethods(finalPool, methodNameById);
  const resolvedBefore = beforeTimeline.map((entry) => resolveTimelineEntry(entry, methodNameById));
  const resolvedPostTerminator = postTerminatorItems.map((entry) => resolveTimelineEntry(entry, methodNameById));

  return (
    <div style={{ marginBottom: 12 }}>
      <span style={inlineSectionLabelStyle}>Execution trace</span>
      {allRules.length === 0 ? (
        <Text type="secondary">No active rules in the ruleset.</Text>
      ) : (
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          <div style={{ marginBottom: 18 }}>
            <CollapsiblePool
              panelKey="starting"
              label={`Starting service method list (${initialPool.length})`}
              methods={initialPoolMethods}
              selectedMethodId={selectedMethodId}
            />
          </div>

          <Timeline className={styles.flush}>
            {resolvedBefore.map((entry) => (
              <RuleTimelineItem key={entry.rule.ruleId} entry={entry} />
            ))}
          </Timeline>

          <div style={{ marginTop: 12 }}>
            <CollapsiblePool
              panelKey="final"
              label={`Ending service method list (${finalPool.length})`}
              methods={finalPoolMethods}
              selectedMethodId={selectedMethodId}
            />
          </div>

          {resolvedPostTerminator.length > 0 ? (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                border: '1px dashed rgba(0,0,0,0.15)',
                borderRadius: 6,
                background: 'rgba(0,0,0,0.02)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                <BranchesOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  If processing continued, this would have been the result for these rules:
                </Text>
              </div>
              <Timeline className={styles.flush}>
                {resolvedPostTerminator.map((entry) => (
                  <RuleTimelineItem key={entry.rule.ruleId} entry={entry} />
                ))}
              </Timeline>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
